import { useState } from 'react';
import useSWR from 'swr';
import Button from '../../Button';
import DatePicker from '../DatePicker';
import Dialog from '../Dialog';
import styles from './CaregiverStatisticsDialog.module.scss';
import moment from 'moment';
import { CaregiverPickerPure } from '../InboxV2/CaregiverPicker';
import { useInbox } from '../../../contexts/InboxProvider';
import HasAuthority from '../../HasAuthority';
import SkeletonComponent from '../../SkeletonComponent';
import CalendarIcon from '../../../assets/icons/calendar-month.svg?react';
import DetailedErrorMessage from '../DetailedErrorMessage';

type Props = {
  onClose: () => void;
};

export default function CaregiverStatisticsDialog({ onClose }: Props) {
  const { caregivers, role, fetcher, user } = useInbox();
  const startOfMonth = moment(new Date()).startOf('month').toDate();
  const [selectedFromDate, setSelectedFromDate] = useState<Date>(startOfMonth);
  const [selectedToDate, setSelectedToDate] = useState<Date>(new Date());
  const [customizePeriod, setCustomizePeriod] = useState(false);
  const [selectedCaregiver, setSelectedCaregiver] = useState<SystemUser>();

  const caregiverId = selectedCaregiver?.guid || user.guid;
  const searchParams = new URLSearchParams();
  searchParams.append('from', moment(selectedFromDate).format('YYYY-MM-DD'));
  searchParams.append('to', moment(selectedToDate).format('YYYY-MM-DD'));

  const { data, isLoading, error } = useSWR<CaregiverFinalNoteStatistics>(
    `/clinic/statistics/encounter/${caregiverId}?${searchParams.toString()}`,
    fetcher
  );

  const numNotChargedToRegion =
    data?.totalFinalNotes &&
    data?.totalFinalNotesChargedToRegion &&
    data.totalFinalNotes - data.totalFinalNotesChargedToRegion;

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title="Din statistik"
      actions={<Button onClick={onClose}>OK</Button>}
      hint={
        !error && (
          <span>
            <sup className="fs-14">*</sup>Denna rapport visar enbart slutanteckningar utifrån hantering i Clinic. För
            löneunderlag appliceras ytterligare logik, exempelvis förekomst av dubbeldebiteringar, maxdebiteringar per
            patient eller manuella krediteringar av kontroller.
          </span>
        )
      }
    >
      {error ? (
        <DetailedErrorMessage error={error} />
      ) : (
        <div className={styles.container}>
          <div className="mb-20">
            <div className="grey-700 fs-16">
              Du är {role === 'DOCTOR' ? 'PAL' : 'PAS'} för <strong>{data?.totalNumberOfPatients}</strong> patienter.
            </div>
          </div>
          <div>
            <h3>Slutanteckningsstatistik för {!customizePeriod ? moment(new Date()).format('MMMM') : 'vald period'}</h3>
          </div>
          <div className={styles.wrapper}>
            <dl>
              <dt>
                {isLoading ? (
                  <Loader />
                ) : typeof data?.totalFinalNotes === 'number' ? (
                  data.totalFinalNotes.toLocaleString('sv-SE')
                ) : !selectedCaregiver ? (
                  '-'
                ) : (
                  0
                )}
              </dt>
              <dd>Slutanteckningar totalt</dd>
            </dl>
            <dl>
              <dt>
                {isLoading ? (
                  <Loader />
                ) : typeof data?.totalFinalNotesChargedToRegion === 'number' ? (
                  data.totalFinalNotesChargedToRegion.toLocaleString('sv-SE')
                ) : !selectedCaregiver ? (
                  '-'
                ) : (
                  0
                )}
                <sup>*</sup>
              </dt>
              <dd>Kvalificerade</dd>
            </dl>
            <dl>
              <dt>
                {isLoading ? (
                  <Loader />
                ) : data?.totalFinalNotes && data.totalFinalNotesChargedToRegion ? (
                  <span>
                    {`${Math.round((data.totalFinalNotesChargedToRegion / data.totalFinalNotes) * 100)}`}{' '}
                    <span className="fs-18">%</span>
                  </span>
                ) : (
                  '-'
                )}
              </dt>
              <dd>
                <div>Andel kvalificerade</div>
                {numNotChargedToRegion ? (
                  <div>
                    ({numNotChargedToRegion.toLocaleString('sv-SE')} ej kvalificerad
                    {numNotChargedToRegion > 1 ? 'e' : ''})
                  </div>
                ) : null}
              </dd>
            </dl>
          </div>

          <div className={styles.controls}>
            <div className={styles.picker}>
              <HasAuthority authority="medicalManagement">
                {caregivers.length ? (
                  <>
                    <div>Visar siffror för</div>
                    <CaregiverPickerPure
                      caregivers={caregivers}
                      defaultSelected={selectedCaregiver}
                      onSelectCaregiver={setSelectedCaregiver}
                      label="Välj vårdgivare"
                    />
                  </>
                ) : null}
              </HasAuthority>
            </div>
            <div className="vertical-align">
              {!customizePeriod ? (
                <Button variant="text" onClick={() => setCustomizePeriod(!customizePeriod)}>
                  <CalendarIcon /> Välj datumintervall
                </Button>
              ) : (
                <div className="flex space-x-1 flex-end">
                  <div>
                    <div>Från och med</div>
                    <DatePicker
                      selectedDate={selectedFromDate}
                      onSelectDate={setSelectedFromDate}
                      popperPlacement="bottom"
                      selectsStart
                      startDate={selectedFromDate}
                      endDate={selectedToDate}
                      minDate={moment(new Date()).subtract(999, 'year').toDate()}
                    />
                  </div>
                  <div>
                    <div>Till och med</div>
                    <DatePicker
                      selectedDate={selectedToDate}
                      onSelectDate={setSelectedToDate}
                      popperPlacement="bottom"
                      selectsEnd
                      startDate={selectedFromDate}
                      endDate={selectedToDate}
                      minDate={selectedFromDate}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}

const Loader = () => <SkeletonComponent width="40px" height="40px" margin="0" display="inline-block" />;
